import { ensureCsrfCookie, readCsrfCookie } from './csrf'

/**
 * /auth/me bootstrap endpoint. Not exposed via Connect because it's called
 * before the app has a transport (circular dependency on the auth guard).
 */
export interface MeUser {
  id: string
  username: string
  email: string
  display_name?: string
  avatar_url?: string
  // Set when the user has an in-flight DeleteAccountWorkflow during the
  // 10-day grace period. SPA renders a global banner offering "cancel
  // deletion" until this clears (cancel) or the user is soft-deleted.
  pending_deletion_at?: string
}

export interface MeOrganization {
  id: string
  name: string
  display_name?: string
  // Subscription-driven lifecycle state (migration 000601). Empty
  // string or "active" means normal operation. "grace" means the
  // org's Stripe subscription lapsed AND it's over the free tier;
  // 30-day countdown is grace_expires_at. "soft_deleted" means the
  // grace expired and the org is read-only / hidden until admin
  // restores. SPA renders an org-level banner per affected org.
  state?: 'active' | 'grace' | 'soft_deleted' | string
  grace_started_at?: string
  grace_expires_at?: string
  soft_deleted_at?: string
}

export interface MeMembership {
  organization: MeOrganization
  role: string
  joined_at: string
  member_count: number
}

export interface MeResponse {
  user: MeUser | null
  memberships: MeMembership[]
  session_expiry?: string
  // Server-computed from the admin allowlist; the list itself never
  // leaves the server. Gates the Admin link only.
  can_access_admin?: boolean
}

// CSRF double-submit: state-changing /auth/* POSTs must echo the wippy_csrf
// cookie back as X-CSRF-Token. /auth/me is a GET so it's exempt.
function csrfHeaders(): Record<string, string> {
  const csrf = readCsrfCookie()
  return csrf ? { 'X-CSRF-Token': csrf } : {}
}

export async function fetchMe(): Promise<MeResponse | null> {
  const res = await fetch('/auth/me', {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw new Error(`GET /auth/me: ${res.status}`)
  const body = (await res.json()) as MeResponse
  // Server always returns 200 now; anonymous visitors get { user: null }
  // instead of a 401. Preserve the old "no session" contract by returning
  // null, so auth store `apply(null)` sets user=null and isAuthenticated=false.
  if (body.user === null) return null
  return body
}

export type RefreshSessionResult = 'refreshed' | 'invalid' | 'unavailable'

export async function refreshSession(): Promise<RefreshSessionResult> {
  const csrf = await ensureCsrfCookie()
  const res = await fetch('/auth/refresh', {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-CSRF-Token': csrf },
  })
  if (res.ok) return 'refreshed'
  if (res.status >= 500) return 'unavailable'
  return 'invalid'
}

export async function logout(): Promise<void> {
  await fetch('/auth/logout', {
    method: 'POST',
    credentials: 'include',
    headers: csrfHeaders(),
  })
}
